import "./StandardSpecificSection.css";

import { EditGreyIcon } from "../../../../../../../assets";
import { Button, Column, Divider, ErrorBox, Row } from "../../../../../../../widget";
import { ProcessComponents, ProcessSteps } from "./components";
import { StandardSpecificDefinition, Step } from "./types";
import { useStandardSpecificSection } from "./useStandardSpecificSection";
import { stepHasDisplayableComponents, stepHasEditableComponents } from "./utils";

interface StandardSpecificSectionProps {
  standardSpecificDefinition: StandardSpecificDefinition;
  hasWriteProjectDetailsPermission: boolean;
  centerTextHidden?: boolean;
}

export const StandardSpecificSection = ({
  standardSpecificDefinition,
  hasWriteProjectDetailsPermission,
  centerTextHidden = true,
}: StandardSpecificSectionProps): JSX.Element => {
  const { data, errors, expressionContext, onSave, onCancel, updateData, isOnSaveLoading, isReadOnly, setIsReadOnly } =
    useStandardSpecificSection();
  const filteredSteps = standardSpecificDefinition?.steps?.filter((step: Step) =>
    stepHasDisplayableComponents(step, expressionContext)
  );

  return (
    <>
      {filteredSteps &&
        filteredSteps.map((step: Step, stepIdx: number) => {
          const filteredChildSteps =
            step.steps &&
            step.steps.filter((childStep: Step) => stepHasDisplayableComponents(childStep, expressionContext));
          return (
            <div key={step.key}>
              <Row spacingV="2xl">
                <Column span={10} justify="between">
                  <h2>{step.label}</h2>
                </Column>
                <Column span={2} className="editStandardColumn">
                  {isReadOnly &&
                    hasWriteProjectDetailsPermission &&
                    stepHasEditableComponents(step, expressionContext) && ( // Only show the icon if the step has editable components
                      <div className="editStandardButton">
                        <EditGreyIcon onClick={() => setIsReadOnly(false)} />
                      </div>
                    )}
                </Column>
              </Row>
              {!!errors[step.key]?.length && (
                <Row spacingV="ml">
                  <Column span={4}>
                    <ErrorBox
                      title="Please solve the below issues before saving"
                      text={errors[step.key].map((error) => error.message)}
                      animation={Object.keys(errors)[0] === step.key}
                    />
                  </Column>
                </Row>
              )}
              {filteredChildSteps &&
                filteredChildSteps.length > 0 &&
                ProcessSteps({
                  steps: filteredChildSteps,
                  key: step.key,
                  data,
                  expressionContext,
                  updateData,
                  displayOnlyReadonly: isReadOnly,
                  centerTextHidden,
                })}
              {step.components &&
                ProcessComponents({
                  components: step.components,
                  key: step.key,
                  expressionContext,
                  updateData,
                  displayOnlyReadonly: isReadOnly,
                })}
              {filteredSteps && stepIdx !== filteredSteps.length - 1 && (
                <Divider type="horizontal" className="StandardSpecificSectionDivider" />
              )}
            </div>
          );
        })}
      {filteredSteps?.some((step: Step) => stepHasEditableComponents(step, expressionContext)) && !isReadOnly && (
        <div className="StandardSpecificButtonContainer">
          <Button text="Save" onClick={onSave} isLoading={isOnSaveLoading} />
          <Button text="Cancel" onClick={onCancel} variant="secondary" />
        </div>
      )}
    </>
  );
};
